<script>
import bem from '@/utils/bem';
import { mapActions } from 'vuex';
import NewFrameOption from '@/components/modals/New-frame-option.vue';
import {
  PlusOutlined,
  SwapOutlined,
} from '@ant-design/icons-vue';

const b = bem('options-grid');

export default {
  components: {
    NewFrameOption,
    PlusOutlined,
    SwapOutlined,
  },
  props: {
    groups: { type: Array, default: () => [] },
    frame: { type: Object, default: () => ({}) },
  },
  emits: ['select', 'updated'],
  data() {
    return {
      showModal: false,
    };
  },
  methods: {
    ...mapActions({
      deleteAction: 'createTemplate/deleteFrameOption',
    }),
    b(firstArg, secondArg) {
      return b(firstArg, secondArg);
    },
    onOptionClick(option) {
      this.$emit('select', option);
    },
    onCreate() {
      this.showModal = true;
    },
    orientationLabel(val) {
      if (val === 'vertical') return 'V';
      if (val === 'horizontal') return 'H';
      return '—';
    },
  },
};
</script>

<template>
  <div :class="b()">
    <div :class="b('header')">
      <h2 :class="b('title')">
        {{ frame.displayName || 'Options' }}
      </h2>
      <a-button type="primary" @click="onCreate">
        <template #icon><PlusOutlined /></template>
        New option
      </a-button>
    </div>

    <!-- Grouped by size -->
    <div
      v-for="group in groups"
      :key="group.sizeId"
      :class="b('size-group')"
    >
      <div :class="b('size-header')">
        <h3 :class="b('size-title')">{{ group.sizeName }}</h3>
        <a-tag>
          {{ group.options.length }}
          variant{{ group.options.length !== 1 ? 's' : '' }}
        </a-tag>
      </div>

      <div :class="b('grid')">
        <div
          v-for="option in group.options"
          :key="option.id"
          :class="b('card', {
            'out-of-stock': !option.inStock,
          })"
          role="button"
          tabindex="0"
          @click="onOptionClick(option)"
          @keydown.enter="onOptionClick(option)"
        >
          <!-- Orientation indicator -->
          <div :class="b('card-orientation', {
            vertical: option.frameOrientation === 'vertical',
            horizontal: option.frameOrientation === 'horizontal',
          })">
            <SwapOutlined
              v-if="option.frameOrientation === 'vertical'"
              :rotate="90"
            />
            <SwapOutlined v-else />
            <span>
              {{ option.frameOrientation || 'n/a' }}
            </span>
          </div>

          <!-- Card content -->
          <div :class="b('card-body')">
            <div :class="b('card-color')">
              <span
                v-if="option.frameColor?.colorHex"
                :class="b('color-dot')"
                :style="{
                  background: option.frameColor.colorHex,
                }"
              />
              <span>
                {{ option.frameColor?.name || 'n/a' }}
              </span>
            </div>

            <div :class="b('card-price')">
              ${{ option.price || 0 }}
              <span
                v-if="option.additionalDiscount"
                :class="b('discount')"
              >
                -{{ option.additionalDiscount }}%
              </span>
            </div>
          </div>

          <!-- Status badges -->
          <div :class="b('card-badges')">
            <a-tag
              v-if="option.isDefaultOption"
              color="blue"
              :class="b('badge')"
            >
              Default
            </a-tag>
            <a-tag
              v-if="!option.inStock"
              color="red"
              :class="b('badge')"
            >
              Out of stock
            </a-tag>
            <a-tag
              v-if="option.isPersonalize"
              color="purple"
              :class="b('badge')"
            >
              Personalize
            </a-tag>
          </div>
        </div>
      </div>
    </div>

    <a-empty
      v-if="groups.length === 0"
      description="No options configured for this frame"
    />

    <NewFrameOption
      v-if="showModal"
      v-model:show="showModal"
    />
  </div>
</template>

<style scoped lang="scss">
@import '@/styles/variables';

.options-grid {
  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 24px;
  }

  &__title {
    font-size: 22px;
    font-weight: 700;
    color: var(--Text-Primary);
    margin: 0;
  }

  &__size-group {
    margin-bottom: 32px;
  }

  &__size-header {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    padding-bottom: 8px;
    border-bottom: 2px solid var(--Border-Light);
  }

  &__size-title {
    font-size: 18px;
    font-weight: 600;
    color: var(--Text-Primary);
    margin: 0;
  }

  &__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 16px;
  }

  &__card {
    background: var(--Background-Main);
    border: 1px solid var(--Border-Light);
    border-radius: 12px;
    padding: 16px;
    cursor: pointer;
    transition: all 0.2s ease;
    display: flex;
    flex-direction: column;
    gap: 12px;

    &:hover {
      border-color: var(--Button-Primary);
      box-shadow: var(--Shadow-Medium);
      transform: translateY(-2px);
    }

    &:focus-visible {
      outline: 2px solid var(--Button-Primary);
      outline-offset: 2px;
    }

    &_out-of-stock {
      opacity: 0.5;
    }
  }

  &__card-orientation {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    color: var(--Text-Secondary);
    text-transform: capitalize;

    &_vertical {
      color: var(--Info);
    }
    &_horizontal {
      color: var(--Success);
    }
  }

  &__card-body {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  &__card-color {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 14px;
    color: var(--Text-Primary);
  }

  &__color-dot {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    border: 1px solid var(--Border-Color);
    flex-shrink: 0;
  }

  &__card-price {
    font-size: 18px;
    font-weight: 700;
    color: var(--Text-Primary);
  }

  &__discount {
    font-size: 12px;
    font-weight: 500;
    color: var(--Error);
    margin-left: 4px;
  }

  &__card-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__badge {
    font-size: 11px;
    margin: 0;
  }
}
</style>
